import React, { ReactElement, useContext } from 'react'
import { LanguageContext } from '../../../Common/Contexts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import FormRow from '../../../Common/Components/FormRow'
import Checkbox from '../../../Common/Components/Checkbox'
import { ReportContentRowsSettingsLanguage } from '../../../../../i18n'
/**
 * Component properties type.
 */
type Props = {
    isShowingHeaderRowEnabled: boolean
    setIsShowingHeaderRowEnabled: (value: boolean) => void
    isShowingFooterRowEnabled: boolean
    setIsShowingFooterRowEnabled: (value: boolean) => void
    isShowingContentRowsEnabled: boolean
    setIsShowingContentRowsEnabled: (value: boolean) => void
    isShowingSpacerRowsEnabled: boolean
    setIsShowingSpacerRowsEnabled: (value: boolean) => void
    disableSettings?: boolean
}

/**
 * Component that renders form row with report rows content display settings.
 * @param isShowingHeaderRowEnabled  Boolean defining the state of the Show Header Row setting.
 * @param setIsShowingHeaderRowEnabled Setter for the property with identical name.
 * @param isShowingFooterRowEnabled Boolean defining the state of the Show Footer Row setting.
 * @param setIsShowingFooterRowEnabled Setter for the property with identical name.
 * @param isShowingContentRowsEnabled Boolean defining the state of the Show Content Rows setting.
 * @param setIsShowingContentRowsEnabled Setter for the property with identical name.
 * @param isShowingSpacerRowsEnabled Boolean defining the state of the Show Spacer Rows setting.
 * @param setIsShowingSpacerRowsEnabled Setter for the property with identical name.
 * @param disableSettings Whether the settings (checkboxes) are disabled.
 */
const ReportContentRowsSettings: React.FC<Props> = ({
    isShowingHeaderRowEnabled,
    setIsShowingHeaderRowEnabled,
    isShowingFooterRowEnabled,
    setIsShowingFooterRowEnabled,
    isShowingContentRowsEnabled,
    setIsShowingContentRowsEnabled,
    isShowingSpacerRowsEnabled,
    setIsShowingSpacerRowsEnabled,
    disableSettings
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ReportContentRowsSettingsLanguage')

    // On change state handler for the header row checkbox.
    const onHeaderRowChange = (value: boolean): void => {
        // Set checkbox flag.
        setIsShowingHeaderRowEnabled(value)
        // Check if for header/footer state.
        if (!isShowingFooterRowEnabled && value === false) {
            setIsShowingContentRowsEnabled(true)
            setIsShowingSpacerRowsEnabled(false)
        } else if (isShowingFooterRowEnabled && value === true) {
            // Deliberately do nothing in this case.
        } else {
            setIsShowingContentRowsEnabled(true)
        }
    }
    // On change state handler for the footer row checkbox.
    const onFooterRowChange = (value: boolean): void => {
        // Set checkbox flag.
        setIsShowingFooterRowEnabled(value)
        // Check if for header/footer state.
        if (!isShowingHeaderRowEnabled && value === false) {
            setIsShowingContentRowsEnabled(true)
            setIsShowingSpacerRowsEnabled(false)
        } else if (value === false) {
            setIsShowingSpacerRowsEnabled(false)
        } else {
            setIsShowingContentRowsEnabled(true)
            setIsShowingSpacerRowsEnabled(true)
        }
    }
    // On change state handler for the content row checkbox.
    const onContentRowChange = (value: boolean): void => {
        // Set checkbox flag.
        setIsShowingContentRowsEnabled(value)
        // Check if for header/footer state.
        if (value === false) {
            setIsShowingSpacerRowsEnabled(false)
        } else if (value === true && isShowingFooterRowEnabled) {
            // If both content and footer checkboxes are enabled, spacer row checkbox should be enabled and checked.
            setIsShowingSpacerRowsEnabled(true)
        } else {
            setIsShowingSpacerRowsEnabled(false)
        }
    }

    return (
        <FormRow isInline className={'report-content-row-settings'}>
            <Checkbox
                className='left-aligned-checkbox show-header-row'
                disabled={disableSettings}
                labelLeft={language.get(ReportContentRowsSettingsLanguage.ShowHeaderRowLabel)}
                onChange={onHeaderRowChange}
                isChecked={isShowingHeaderRowEnabled}
            />
            <Checkbox
                className='left-aligned-checkbox show-footer-row'
                disabled={disableSettings}
                labelLeft={language.get(ReportContentRowsSettingsLanguage.ShowFooterRowLabel)}
                onChange={onFooterRowChange}
                isChecked={isShowingFooterRowEnabled}
            />
            <Checkbox
                className='left-aligned-checkbox show-content-rows'
                labelLeft={language.get(ReportContentRowsSettingsLanguage.ShowContentRowsLabel)}
                onChange={onContentRowChange}
                isChecked={isShowingContentRowsEnabled}
                disabled={disableSettings || (!isShowingHeaderRowEnabled && !isShowingFooterRowEnabled)}
            />
            <Checkbox
                className='left-aligned-checkbox show-spacer-rows'
                labelLeft={language.get(ReportContentRowsSettingsLanguage.ShowSpacerRowsLabel)}
                onChange={setIsShowingSpacerRowsEnabled}
                isChecked={isShowingSpacerRowsEnabled}
                disabled={disableSettings || !isShowingFooterRowEnabled || !isShowingContentRowsEnabled}
            />
        </FormRow>
    )
}

export default ReportContentRowsSettings
